import { ampli, type OnboardingStepInteractionProperties } from '@magicschool/ampli-client';
import { PROFILE_BUILDER_STEPS } from '@/components/ProfileBuilder/constants';

// ---------------------------------------------------------------------------
// Enums — mirror every Ampli-defined enum type for use in calling code
// ---------------------------------------------------------------------------

export enum ProfileUpdateSurface {
  RefreshModal = 'profile_refresh_modal',
  Builder = 'profile_builder',
  Page = 'profile_page',
}

export enum OnboardingStepDirection {
  Back = 'back',
  Next = 'next',
}

// Ampli's fields_changed is an untyped string[] — this module owns the constraint.
export type ProfileField = 'role' | 'school' | 'grades' | 'subjects' | 'location' | 'name';

export type ProfileFieldsSnapshot = {
  selectedProfessionId?: string;
  institutionIds: string[];
  selectedGrades: string[];
  selectedSubjects: string[];
  city?: string;
  stateProvince?: string;
  countryCode?: string;
  fullName?: string;
  studentFriendlyName?: string;
};

// ---------------------------------------------------------------------------
// Mappers
// ---------------------------------------------------------------------------

const sameMembers = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((value, index) => value === sortedB[index]);
};

export const computeChangedProfileFields = (pristine: ProfileFieldsSnapshot, current: ProfileFieldsSnapshot): ProfileField[] => {
  const changed: ProfileField[] = [];

  if (pristine.selectedProfessionId !== current.selectedProfessionId) changed.push('role');
  if (!sameMembers(pristine.institutionIds, current.institutionIds)) changed.push('school');
  if (!sameMembers(pristine.selectedGrades, current.selectedGrades)) changed.push('grades');
  if (!sameMembers(pristine.selectedSubjects, current.selectedSubjects)) changed.push('subjects');
  if (pristine.city !== current.city || pristine.stateProvince !== current.stateProvince || pristine.countryCode !== current.countryCode) {
    changed.push('location');
  }
  if (pristine.fullName !== current.fullName || pristine.studentFriendlyName !== current.studentFriendlyName) {
    changed.push('name');
  }

  return changed;
};

type OnboardingStep = OnboardingStepInteractionProperties['onboarding_step'];

// PROFILE_BUILDER_STEPS.BEST_PRACTICES is 'best-practices' (hyphen); Ampli's enum value is
// 'best_practices' (underscore) — an explicit mapping, not a cast, is needed to bridge the two.
const ONBOARDING_STEP_MAP: Record<string, OnboardingStep> = {
  [PROFILE_BUILDER_STEPS.WELCOME]: 'welcome',
  [PROFILE_BUILDER_STEPS.PROFESSION]: 'profession',
  [PROFILE_BUILDER_STEPS.LOCATION]: 'location',
  [PROFILE_BUILDER_STEPS.INSTITUTION]: 'institution',
  [PROFILE_BUILDER_STEPS.GRADES]: 'grades',
  [PROFILE_BUILDER_STEPS.SUBJECTS]: 'subjects',
  [PROFILE_BUILDER_STEPS.NAME]: 'name',
  [PROFILE_BUILDER_STEPS.BEST_PRACTICES]: 'best_practices',
};

export const mapOnboardingStep = (step: string): OnboardingStep | null => ONBOARDING_STEP_MAP[step] ?? null;

// Analytics events should carry human-readable names, not UUIDs — used to resolve
// institution/grade/subject ids to their names before sending. Custom (non-UUID) entries (e.g. a
// typed-in institution or subject) store the name directly as their "id" — fall back to the id
// itself when no matching record is found.
export const resolveNamesFromIds = (ids: string[], records: Array<{ id: string; name?: string | null }>): string[] =>
  ids.filter(Boolean).map((id) => records.find((record) => record.id === id)?.name ?? id);

// ---------------------------------------------------------------------------
// Wrappers
// ---------------------------------------------------------------------------

export const trackProfileUpdateImpression = (params: {
  surface: ProfileUpdateSurface;
  hasPriorCompletedProfile: boolean;
  isDismissable?: boolean;
}): void => {
  ampli.profileUpdateImpression({
    profile_update_surface: params.surface,
    has_prior_completed_profile: params.hasPriorCompletedProfile,
    is_dismissable: params.isDismissable,
  });
};

// onboarding_surface only ever has one value today (see the enum's own doc comment) — hardcoded
// here rather than threaded through every caller.
export const trackOnboardingStepInteraction = (params: {
  step: string;
  direction: OnboardingStepDirection;
  hasPriorCompletedProfile: boolean;
}): void => {
  const onboardingStep = mapOnboardingStep(params.step);
  if (!onboardingStep) return;
  ampli.onboardingStepInteraction({
    onboarding_step: onboardingStep,
    onboarding_step_direction: params.direction,
    onboarding_surface: 'profile_builder',
    has_prior_completed_profile: params.hasPriorCompletedProfile,
  });
};

// Only fires for non-blocking dismissals (X, ESC, outside-click on a dismissable modal) — hard
// blocked-close attempts have no corresponding event. hardDismissalsUsed is only meaningful for
// the Refresh Modal's hard phase; other surfaces omit it.
export const trackProfileUpdateClosed = (params: { surface: ProfileUpdateSurface; hardDismissalsUsed?: number }): void => {
  ampli.profileUpdateClosed({ profile_update_surface: params.surface, hard_dismissals_used: params.hardDismissalsUsed });
};

export const trackProfileUpdated = (params: {
  surface: ProfileUpdateSurface;
  fieldsChanged: ProfileField[];
  profession: string;
  grades: string[];
  subjects: string[];
  institutions?: string[];
  city?: string;
  stateProvince?: string;
  countryCode?: string;
  fullName?: string;
  studentFriendlyName?: string;
  hasPriorCompletedProfile: boolean;
}): void => {
  ampli.profileUpdated({
    profile_update_surface: params.surface,
    fields_changed: params.fieldsChanged,
    profession: params.profession,
    grades: params.grades,
    // Ampli's subjects requires unique items — some callers (e.g. ProfileBuilder) can pass a
    // custom subject that's already present in the selected-subjects list.
    subjects: Array.from(new Set(params.subjects)),
    institutions: params.institutions,
    city: params.city,
    state_province: params.stateProvince,
    country_code: params.countryCode,
    full_name: params.fullName,
    student_friendly_name: params.studentFriendlyName,
    has_prior_completed_profile: params.hasPriorCompletedProfile,
  });
};
